import { Trade } from '@uniswap/router-sdk'
import { Currency, Percent, TradeType } from '@uniswap/sdk-core'
import { SwapRouter } from '@uniswap/universal-router-sdk'
import { toHex } from '@uniswap/v3-sdk'
import BNJS from 'bignumber.js'
import { UNIVERSAL_ROUTER_ADDRESS } from 'constants/addresses'
import { UNAVAILABLE } from 'constants/misc'
import { BigNumber, constants, Contract } from 'ethers/lib/ethers'
import { parseUnits } from 'ethers/lib/utils'
import { useCallback, useEffect, useState } from 'react'
import isZero from 'utils/isZero'
import { useAccount } from 'wagmi'

import { useEthersProvider } from '../connection/wallet-connect'
import { useNativeTokenPrice } from './useNativeTokenPrice'
import { PermitSignature } from './usePermitAllowance'
import useTransactionDeadline from './useTransactionDeadline'

const ERC20_APPROVE_ABI = ['function approve(address spender, uint256 amount) external returns (bool)']

type SwapGasCostProps = {
  trade: Trade<Currency, Currency, TradeType> | undefined
  slippageTolerance: Percent
  permit?: PermitSignature
  inputCurrency?: Currency | null
  isApprovedOrNative?: boolean
}

export const useSwapGasCostUsd = ({
  trade,
  slippageTolerance,
  permit,
  inputCurrency,
  isApprovedOrNative = false,
}: SwapGasCostProps) => {
  const [gasCostUsd, setGasCostUsd] = useState(0)
  const deadline = useTransactionDeadline()
  const { address: account, chainId } = useAccount()
  const provider = useEthersProvider()

  const { priceUsd: nativeTokenPriceUsd } = useNativeTokenPrice()

  const calculateGasCostUsd = useCallback(
    (gasEstimate: BigNumber, gasPrice: BigNumber): number => {
      const maxPriorityFeePerGas = parseUnits('2', 'gwei')
      const gasCost = gasPrice.add(maxPriorityFeePerGas).mul(gasEstimate)

      const resultRaw = new BNJS(gasCost.toString()).multipliedBy(nativeTokenPriceUsd).dividedBy(10 ** 18)

      return resultRaw.toNumber()
    },
    [nativeTokenPriceUsd]
  )

  const getSwapGasCostUsd = useCallback(async () => {
    try {
      if (!account || !chainId || !provider || !trade) return

      const gasPrice = await provider.getGasPrice()

      if (!isApprovedOrNative && inputCurrency?.isToken) {
        const tokenContract = new Contract(inputCurrency.address, ERC20_APPROVE_ABI, provider)
        const spender = UNIVERSAL_ROUTER_ADDRESS[chainId]
        const gasEstimate = await tokenContract.estimateGas.approve(spender, constants.MaxUint256, {
          from: account,
        })
        setGasCostUsd(calculateGasCostUsd(gasEstimate, gasPrice))
        return
      }

      const { calldata: data, value } = SwapRouter.swapERC20CallParameters(trade, {
        slippageTolerance,
        deadlineOrPreviousBlockhash: deadline?.toString(),
        inputTokenPermit: permit,
      })

      const tx = {
        from: account,
        to: UNIVERSAL_ROUTER_ADDRESS[chainId],
        data,
        ...(value && !isZero(value) ? { value: toHex(value) } : {}),
      }

      const gasEstimate = await provider.estimateGas(tx)
      setGasCostUsd(calculateGasCostUsd(gasEstimate, gasPrice))
    } catch (err) {
      console.error('getSwapGasCostUsd error:', err)
      setGasCostUsd(UNAVAILABLE)
    }
  }, [
    account,
    chainId,
    deadline,
    permit,
    provider,
    slippageTolerance,
    trade,
    isApprovedOrNative,
    inputCurrency,
    calculateGasCostUsd,
  ])

  useEffect(() => {
    void getSwapGasCostUsd()
  }, [getSwapGasCostUsd])

  return gasCostUsd
}
